import { createFileRoute, useSearch } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { AiOnboardingChat } from '@/features/chat-onboarding/components/ai-onboarding-chat'

const joinSearchSchema = z.object({
  origin: z.enum(['direct_signup', 'invitation_link', 'data_room']).optional(),
  token: z.string().optional(),
})

export type JoinSearchParams = z.infer<typeof joinSearchSchema>

export const Route = createFileRoute('/_protected/join')({
  component: ChatOnboardingPage,
  validateSearch: joinSearchSchema,
})

type ViewState = 'splash' | 'splash-out' | 'chat' | 'fading-out'

function ChatOnboardingPage() {
  const { origin, token } = useSearch({ from: '/_protected/join' })
  const [view, setView] = useState<ViewState>('splash')
  const [splashMounted, setSplashMounted] = useState(false)
  const logoTargetRef = useRef<HTMLDivElement>(null)
  const [logoTarget, setLogoTarget] = useState<{
    top: number
    left: number
    width: number
    height: number
  } | null>(null)

  // Splash fade-in
  useEffect(() => {
    const timer = requestAnimationFrame(() => setSplashMounted(true))
    return () => cancelAnimationFrame(timer)
  }, [])

  // Measure logo target position once chat is rendered (hidden under splash)
  useEffect(() => {
    if (view === 'splash-out') {
      const raf = requestAnimationFrame(() => {
        if (logoTargetRef.current) {
          const rect = logoTargetRef.current.getBoundingClientRect()
          setLogoTarget({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          })
        }
      })
      return () => cancelAnimationFrame(raf)
    }
  }, [view])

  useEffect(() => {
    if (view === 'splash') {
      const timer = setTimeout(() => setView('splash-out'), 1500)
      return () => clearTimeout(timer)
    }
    if (view === 'splash-out') {
      const timer = setTimeout(() => setView('chat'), 1400)
      return () => clearTimeout(timer)
    }
  }, [view])

  const showSplash = view === 'splash' || view === 'splash-out'
  const showChat = view === 'chat' || view === 'fading-out'

  // Logo positioning
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200

  const targetCenterX = logoTarget ? logoTarget.left + logoTarget.width / 2 : vw / 2
  const targetCenterY = logoTarget ? logoTarget.top + logoTarget.height / 2 : 60

  function getLogoStyle(): React.CSSProperties {
    const base: React.CSSProperties = {
      position: 'fixed',
      zIndex: 51,
      transform: 'translate(-50%, -50%)',
    }

    if (view === 'splash' && !splashMounted) {
      return {
        ...base,
        transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
        top: vh / 2 + 20,
        left: vw / 2,
        opacity: 0,
      }
    }
    if (view === 'splash') {
      return {
        ...base,
        transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
        top: vh / 2,
        left: vw / 2,
        opacity: 1,
      }
    }
    // splash-out (only remaining case since fixed logo is hidden in chat view)
    return {
      ...base,
      transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
      top: targetCenterY,
      left: targetCenterX,
      opacity: 1,
    }
  }

  return (
    <div className="relative min-h-screen bg-[#141414]">
      {/* Splash background overlay */}
      {showSplash && (
        <div
          className="fixed inset-0 z-50 bg-[#141414]"
          style={{
            transition: 'opacity 1s ease-out',
            opacity: view === 'splash-out' ? 0 : 1,
          }}
        />
      )}

      {/* Animated logo — fixed during splash, then inline logo takes over in chat */}
      {showSplash && (
        <img
          src="/seedforge-full-light.svg"
          alt="SeedForge"
          className="pointer-events-none h-14"
          style={getLogoStyle()}
        />
      )}

      {/* Chat — rendered underneath during splash-out so we can measure target */}
      {(view === 'splash-out' || showChat) && (
        <div style={{ opacity: view === 'splash-out' ? 0 : 1 }}>
          <AiOnboardingChat
            logoTargetRef={logoTargetRef}
            started={view === 'chat'}
            initialContext={origin ? { origin, token } : undefined}
          />
        </div>
      )}
    </div>
  )
}
